import { useQuery } from '@graphcommerce/graphql'
import { Image } from '@graphcommerce/image'
import { CountryRegionsDocument } from '@graphcommerce/magento-store'
import { filterNonNullableKeys, FormRow } from '@graphcommerce/next-ui'
import { assertFormGqlOperation, UseFormReturn } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  MenuItem,
  Popper,
  TextField,
} from '@mui/material'
import React, { ChangeEvent, SyntheticEvent, useMemo, useRef, useState } from 'react'
import expandIcon from '../../../assets/images/arrow-up.svg'
import { useGoogleAddressAutocomplete } from '../../../hooks/useGoogleAddressAutocomplete'
import { Dropdown } from '../../Dropdown'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'
import { StandardTextField } from '../../TextInput/TextFieldStyles'
import { FreightFields } from '../FreightFields/FreightFields'
import { AdditionalFields } from './AdditionalFields'

type AddressFieldValues = {
  company?: string
  street?: string
  houseNumber?: string
  countryCode?: string
  regionId?: string | number
  region?: string | number
  postcode?: string
  city?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AddressFieldsProps = {
  addressType?: 'shipping' | 'billing'
  form: UseFormReturn<any>
  readOnly?: boolean
  regionFieldName?: 'region' | 'regionId'
  isFreight?: boolean
  telephoneField?: React.ReactNode
  nameFields?: React.ReactNode
}

const CustomPopper = (props) => (
  <Popper
    {...props}
    placement='bottom-start'
    sx={{
      width: 'fit-content',
    }}
  />
)

export function AddressFields(props: AddressFieldsProps) {
  const {
    addressType = 'shipping',
    form,
    readOnly,
    regionFieldName = 'regionId',
    isFreight = false,
    telephoneField,
    nameFields,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const [chosenValue, setChosenValue] = useState('#')
  const [currentStreet, setCurrentStreet] = useState(form?.getValues()?.street)
  const [inputStreet, setInputStreet] = useState(form?.getValues()?.street)

  const { placePredictions, getPlaceDetails } = useGoogleAddressAutocomplete({
    // inputValue: chosenValue === inputRef.current?.value ? '' : inputRef.current?.value,
    inputValue: currentStreet,
    enabled: true,
  })

  const countryQuery = useQuery(CountryRegionsDocument, { fetchPolicy: 'cache-and-network' })
  const countries = countryQuery.data?.countries ?? countryQuery.previousData?.countries

  assertFormGqlOperation<AddressFieldValues>(form)
  const { watch, required, valid, control, register, formState } = form

  const country = watch('countryCode')
  const regionId = watch(regionFieldName)

  const countryList = useMemo(() => {
    const countriesWithLocale = (countries ?? [])?.filter((c) => c?.full_name_locale)
    return countriesWithLocale.sort((a, b) =>
      (a?.full_name_locale ?? '')?.localeCompare(b?.full_name_locale ?? ''),
    )
  }, [countries])

  const regionList = useMemo(() => {
    const availableRegions = Object.values(
      countryList.find((c) => c?.two_letter_abbreviation === country)?.available_regions ?? {},
    )
    type Region = (typeof availableRegions)[0]

    const compare: (a: Region, b: Region) => number = (a, b) =>
      (a?.name ?? '')?.localeCompare(b?.name ?? '')

    return availableRegions?.sort(compare)
  }, [country, countryList])

  const regionOptions = filterNonNullableKeys(regionList, ['id', 'name']).map(
    ({ id, name: label }) => ({ value: Number(id), label }),
  )

  const menuItems = regionOptions.map((option) => (
    <MenuItem value={option.value} className='flex w-full items-center justify-between'>
      <span>{option.label}</span>
    </MenuItem>
  ))

  const handlePlaceSelect = (place: any) => {
    if (place.city) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      form.setValue('city', place.city)
    }
    if (place.regionCode) {
      const regionDetails = regionList.find((r) => r?.code == place.regionCode)
      if (regionDetails?.id) form.setValue(regionFieldName, regionDetails?.id)
    }
    if (place.street) {
      form.setValue('street', [place.house || '', place.street || ''].join(' '))
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setCurrentStreet([place.house || '', place.street || ''].join(' '))
      setInputStreet([place.house || '', place.street || ''].join(' '))
    }
    if (place.postCode) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      form.setValue('postcode', place.postCode)
    }
  }

  const handleChangeStreet = (place) => {
    getPlaceDetails(place.place_id, (placeDetails) => {
      handlePlaceSelect(placeDetails)
      setChosenValue(inputRef.current?.value || '')
    })
  }

  return (
    <div className='flex flex-col items-center justify-center gap-y-5 md:items-start md:justify-start'>
      <StandardTextField
        label={<Trans id='Company Name' />}
        variant='outlined'
        error={!!formState.errors.company?.message}
        color='primary'
        required={required.company}
        autoComplete='company'
        className='w-[260px] max-w-[260px]'
        size='small'
        sx={{
          width: '260px',
          minWidth: '260px',
        }}
        {...register('company')}
      />
      {nameFields}
      <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
        <FormRow className='w-[260px] max-w-[260px] py-1' id='address1FormRow'>
          <Autocomplete
            fullWidth
            freeSolo
            size='small'
            defaultValue={currentStreet ?? form?.getValues()?.street}
            value={form?.getValues()?.street ?? currentStreet}
            inputValue={inputStreet}
            options={placePredictions.map((place) => place?.description)}
            componentsProps={{ popper: { style: { width: 'fit-content' } } }}
            PopperComponent={CustomPopper}
            onChange={(event: SyntheticEvent<Element, Event>, newValue: string) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              const place = placePredictions.find((p) => p.description === newValue)
              if (place && place?.place_id) {
                getPlaceDetails(place.place_id, (placeDetails) => {
                  handlePlaceSelect(placeDetails)
                })
              }
            }}
            onInputChange={(event: SyntheticEvent<Element, Event>, newInputValue: string) => {
              setInputStreet(newInputValue)
            }}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  '& .MuiAutocomplete-listbox': {
                    '& .MuiAutocomplete-option': {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      fontSize: '12px',
                      width: '100%',
                      minWidth: '100%',
                      whiteSpace: 'nowrap',
                    },
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<Trans id='Address 1' />}
                color='primary'
                sx={{
                  width: '260px',
                  minWidth: '260px',
                  '& .MuiFormLabel-root': {
                    fontSize: '14px',
                  },
                }}
                value={currentStreet}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setCurrentStreet(event?.target?.value)
                  form.setValue('street', event?.target?.value)
                }}
                size='small'
              />
            )}
          />
        </FormRow>
        <FormRow className='w-[260px] max-w-[260px] py-1' id='address2FormRow'>
          <StandardTextField
            label={<Trans id='Address 2' />}
            variant='outlined'
            error={!!formState.errors.houseNumber?.message}
            color='primary'
            autoComplete='address-line2'
            className='outline-material-ui-blue'
            size='small'
            {...register('houseNumber')}
            sx={{
              width: '260px',
              minWidth: '260px',
            }}
            helperText={formState.errors.houseNumber?.message}
          />
        </FormRow>
      </div>
      <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
        <FormRow className='w-[260px] max-w-[260px]' id='cityFormRow'>
          {/* <TextInput
            type='text'
            required={required.city}
            topLabel={<Trans id='City' />}
            className='w-full'
            helperText={formState.errors.city?.message}
            error={!!formState.errors.city?.message}
            {...register('city', { required: i18n._('This field is required') })}
          /> */}
          <StandardTextField
            label={<Trans id='City' />}
            variant='outlined'
            error={!!formState.errors.city?.message}
            color='primary'
            className='outline-material-ui-blue'
            size='small'
            {...register('city', { required: i18n._(/* i18n */ 'This field is required') })}
            sx={{
              width: '260px',
              minWidth: '260px',
            }}
            value={form.getValues().city?.toString() ?? ''}
            helperText={formState.errors.city?.message}
          />
        </FormRow>
        <FormRow className='w-[260px] max-w-[260px]' id='stateFormRow'>
          {regionList.length > 0 && (
            <StandardDropdown
              label='State'
              required={required[regionFieldName]}
              {...register(regionFieldName, {
                required: i18n._(/* i18n */ 'This field is required'),
              })}
              onChange={(option) => {
                if (option?.target?.value)
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  form.setValue(regionFieldName, option?.target?.value?.toString())
              }}
              options={menuItems}
              sx={{
                width: '260px',
                minWidth: '260px',
              }}
              value={form.getValues()[regionFieldName]?.toString() ?? ''}
            />

            // <Dropdown
            //   selectProps={{ name: regionFieldName }}
            //   topLabels={['State*']}
            //   topLabelsClassName='md:pt-[4px] md:pb-[3px]'
            //   className='Type-Large-Regular w-full'
            //   required={required[regionFieldName]}
            //   value={regionId || ''}
            //   {...register(regionFieldName, {
            //     required: i18n._(/* i18n */ 'This field is required'),
            //   })}
            //   helperText={formState.errors[regionFieldName]?.message}
            //   optionLabelCountInMainArea={1}
            //   onChange={(option) => {
            //     if (option.value) form.setValue(regionFieldName, option.value)
            //   }}
            //   options={filterNonNullableKeys(regionList, ['id', 'name']).map(
            //     ({ id, name: label }) => ({ value: Number(id), label }),
            //   )}
            //   alignSelected='left'
            // />
          )}
        </FormRow>
      </div>
      <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
        <div className='w-[260px] max-w-[260px]' id='postCodeFormRow'>
          <StandardTextField
            label={<Trans id='Postal code' />}
            variant='outlined'
            required={required.postcode}
            error={!!formState.errors.postcode?.message}
            color='primary'
            className='outline-material-ui-blue'
            size='small'
            {...register('postcode', { required: i18n._(/* i18n */ 'This field is required') })}
            sx={{
              width: '260px',
              minWidth: '260px',
            }}
            value={form.getValues().postcode?.toString() ?? ''}
            helperText={formState.errors.postcode?.message}
          />
        </div>
        {telephoneField}

        <div className='hidden w-[260px] max-w-[260px]' id='countryCodeFormRow'>
          <Dropdown
            selectProps={{ name: 'countryCode' }}
            topLabels={['Country*']}
            className='Type-Large-Regular w-full'
            topLabelsClassName='md:pt-[6px] md:pb-[3px]'
            required={required.countryCode}
            value={country || 'United States'}
            {...register('countryCode')}
            optionLabelCountInMainArea={1}
            onChange={(option) => form.setValue('countryCode', option.value?.toString())}
            options={filterNonNullableKeys(countryList, [
              'two_letter_abbreviation',
              'full_name_locale',
            ]).map(({ two_letter_abbreviation: id, full_name_locale: label }) => ({
              value: id,
              label,
            }))}
            alignSelected='left'
          />
        </div>
      </div>
      {addressType === 'shipping' && <AdditionalFields form={form} />}
      {addressType === 'shipping' && (
        <Accordion
          className='flex w-full flex-col items-start bg-transparent bg-opacity-0'
          elevation={0}
          disableGutters
          id='freightFieldsForm'
          sx={{
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              <Image
                src={expandIcon}
                alt='expand or close'
                unoptimized
                height={40}
                width={40}
                layout='fixed'
              />
            }
            className='items ml-[-15px] flex w-full flex-row-reverse justify-between border-0 pl-0'
          >
            <span className='Type-XL-Medium'>
              Freight Delivery Details{' '}
              <span className='Type-XL-Regular inline text-medium-gray'>
                - Recommended for large orders
              </span>
            </span>
          </AccordionSummary>
          <FreightFields form={form} />
        </Accordion>
      )}
    </div>
  )
}
